/**
 * 解析 TikTok publishOrEdit 失败信息，映射到站内表单字段，便于标红与定位。
 */

function collectValidationRows(res) {
  const info = res?.info && typeof res.info === 'object' ? res.info : res;
  if (!info || typeof info !== 'object') return [];
  const buckets = [
    info.pre_valid_result,
    info.mcc_valid_result,
    info.preValidResult,
    info.mccValidResult,
  ];
  const out = [];
  buckets.forEach((arr) => {
    if (!Array.isArray(arr)) return;
    arr.forEach((row) => {
      if (!row || typeof row !== 'object') return;
      out.push(row);
    });
  });
  return out;
}

function firstMessage(row) {
  if (Array.isArray(row?.messages)) {
    const hit = row.messages.map((m) => String(m || '').trim()).find(Boolean);
    if (hit) return hit;
  }
  return String(row?.message || row?.msg || '').trim();
}

function mapPlatformFieldToForm(fieldRaw = '', message = '') {
  const field = String(fieldRaw || '').trim().toLowerCase();
  const msg = String(message || '').toLowerCase();
  const blob = `${field} ${msg}`;

  if (/english|英文名称|英文标题|i18n|multi_language_name/.test(blob)) {
    return { field: 'titleEn', section: 'basic' };
  }
  if (/title|product_name|productname|商品名称|货品名称|name_list/.test(blob)) {
    return { field: 'title', section: 'basic' };
  }
  if (/模特|试穿尺码|goodsmodel|sizeSpecName|size_spec|均码/.test(blob)) {
    return { field: 'goodsModels', section: 'spec' };
  }
  if (/外包装形状|packageshape/.test(blob)) {
    return { field: 'packageShape', section: 'size' };
  }
  if (/外包装图|outer.?package.?image|productouterpackageimage/.test(blob)) {
    return { field: 'outerPackageImages', section: 'size' };
  }
  if (/外包装信息不完整|outer packaging information is incomplete|外包装类型|outerpackage|productouterpackage/.test(blob)) {
    return { field: 'packageShape', section: 'size' };
  }
  if (/素材图|materialimg|material_img|2000017/.test(blob)) {
    return { field: 'materialImgUrl', section: 'spec' };
  }
  // 须先于「类目」：平台常写「服饰类目skc轮播图最少为5张」，含「类目」但实际指向主规格图
  if (/轮播|previewimg|carousel|skc.?图|主规格.*(图|图片)|商品图|细节图|sku预览图|thumburl|2000015/.test(blob)
    || (/skc|主规格|sale_attribute/.test(blob) && /图|image|主图/.test(blob))
    || /image|图片|主图/.test(blob)) {
    return { field: 'skcList', section: 'spec' };
  }
  if (/supplier_code(?!_)|商家货号|货号|extcode|ext_code/.test(blob) && !/supplier_sku|卖家sku|商家\s*sku/.test(blob)) {
    return { field: 'skcList', section: 'spec' };
  }
  if (/supplier_sku|卖家sku|商家\s*sku|sku重复|sku 重复/.test(blob)) {
    return { field: 'supplierSku', section: 'spec' };
  }
  if (/category|类目/.test(blob) && !/轮播|skc|主规格|图片|主图/.test(blob)) {
    return { field: 'categoryId', section: 'basic' };
  }
  if (/brand/.test(blob)) {
    return { field: 'brandCode', section: 'basic' };
  }
  if (/product_attribute|类目属性|attribute/.test(blob) && !/sale_attribute|size_attribute/.test(blob)) {
    return { field: 'productAttributes', section: 'attrs' };
  }
  if (/size_attribute|尺码表|sizechart|sizeTemplate/.test(blob)) {
    return { field: 'sizeChart', section: 'spec' };
  }
  if (/skc|主规格|sale_attribute/.test(blob)) {
    return { field: 'skcList', section: 'spec' };
  }
  if (/quantity_info|package_type|件数|包装类型|包装尺寸|weight|重量|体积|最长边/.test(blob)) {
    return { field: 'supply', section: 'size' };
  }
  if (/cost|currency|币种|供货价|price|stock|库存|仓库|warehouse/.test(blob)) {
    return { field: 'supply', section: 'spec' };
  }
  if (/site|站点|shelf|上架|运费|freight/.test(blob)) {
    return { field: 'selectedSites', section: 'sales' };
  }
  if (/description|desc|描述/.test(blob)) {
    return { field: 'description', section: 'basic' };
  }
  return { field: 'form', section: 'basic' };
}

/**
 * @returns {{ field: string, section: string, message: string }[]}
 */
export function extractTiktokPublishFieldErrors(res, fallbackMessage = '') {
  const rows = collectValidationRows(res);
  const errors = [];
  const seen = new Set();

  rows.forEach((row) => {
    const message = firstMessage(row);
    if (!message) return;
    const platformField = String(row?.field || row?.field_name || row?.fieldName || '').trim();
    const mapped = mapPlatformFieldToForm(platformField, message);
    const key = `${mapped.field}::${message}`;
    if (seen.has(key)) return;
    seen.add(key);
    errors.push({
      field: mapped.field,
      section: mapped.section,
      message,
    });
  });

  if (!errors.length) {
    const detail = [res?.msg, res?.message, res?.info?.msg, res?.info?.message, fallbackMessage]
      .map((v) => (v == null ? '' : String(v).trim()))
      .find((v) => v && v.toUpperCase() !== 'OK');
    if (detail) {
      const mapped = mapPlatformFieldToForm('', detail);
      errors.push({
        field: mapped.field,
        section: mapped.section,
        message: detail,
      });
    }
  }
  return errors;
}

/** @returns {Record<string, string>} */
export function fieldErrorsToMap(errors = []) {
  const map = {};
  (Array.isArray(errors) ? errors : []).forEach((item) => {
    if (!item?.field || !item?.message) return;
    if (!map[item.field]) map[item.field] = item.message;
  });
  return map;
}
